/* SafeDeleteDialog — DELETE INTEGRITY UX (locked contract C). Stage 1
 * always calls DELETE WITHOUT force; a 409 {error:"referenced"} renders
 * referenced_by as a readable list with Cancel / "Delete anyway & clean
 * grants". Force deletes clean GRANTS automatically (role.journeys +
 * app.journeys) but never other definitions — those come back in
 * left_dangling and are shown as a final warning stage. */
import React, {
    useEffect,
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    AlertTriangle,
    Link2
} from 'lucide-react';
import {
    describeError,
    extractReferencedBy,
    extractValidatorErrors
} from '../api/appEngine.service';
import {
    notify
} from 'shared/api/notifier';
import type {
    ReferencedBy
} from '../api/types';

type Stage = 'confirm' | 'referenced' | 'dangling';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: React.ReactNode;
    /** Runs the DELETE — called with force=false first, force=true from
     * the referenced stage. Must reject with the axios error. */
    perform: (force: boolean) => Promise<any>;
    /** Fired once the delete landed (after the dangling stage closes). */
    onDeleted: () => void;
};

function refLine(e: ReferencedBy): string {
    const label = e.name ? `'${e.name}'` : `'${e.id || '?'}'`;
    return `${e.type} ${label}${e.where ? ` (${e.where})` : ''}`;
}

function RefList({
    items,
    tone
}: {
    items: ReferencedBy[];
    tone: 'danger' | 'warning';
}) {
    return (
        <ul style={{
            margin: '8px 0 0',
            paddingLeft: 18
        }}>
            {items.map((e, i) => (
                <li key={i} className="aps-mono" style={{
                    fontSize: 'var(--aps-text-xs)',
                    color: tone === 'danger' ? 'var(--aps-text)' : 'var(--aps-warning)',
                    margin: '3px 0'
                }}>
                    {refLine(e)}
                </li>
            ))}
        </ul>
    );
}

export default function SafeDeleteDialog({
    open,
    onOpenChange,
    title,
    description,
    perform,
    onDeleted
}: Props) {
    const [stage, setStage] = useState<Stage>('confirm');
    const [refs, setRefs] = useState<ReferencedBy[]>([]);
    const [dangling, setDangling] = useState<ReferencedBy[]>([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setStage('confirm');
            setRefs([]);
            setDangling([]);
        }
    }, [open]);

    const finish = (result: any) => {
        const left = Array.isArray(result?.left_dangling)
            ? result.left_dangling : [];
        if (left.length > 0) {
            setDangling(left);
            setStage('dangling');
        } else {
            onOpenChange(false);
            onDeleted();
        }
    };

    const run = async (force: boolean) => {
        setBusy(true);
        try {
            finish(await perform(force));
        } catch (err: any) {
            const referenced = extractReferencedBy(err);
            if (referenced) {
                setRefs(referenced);
                setStage('referenced');
            } else {
                const ve = extractValidatorErrors(err);
                notify(ve ? ve.join(' · ') : describeError(err), 'danger');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        {stage === 'referenced'
                            ? <Link2 size={18} color="var(--aps-danger)" />
                            : <AlertTriangle size={18} color={stage === 'dangling'
                                ? 'var(--aps-warning)' : 'var(--aps-danger)'} />}
                        {stage === 'referenced' ? `Still referenced — ${title}`
                            : stage === 'dangling' ? 'Deleted — references left dangling'
                                : title}
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div>
                            {stage === 'confirm' && description}
                            {stage === 'referenced' && (
                                <>
                                    <span>
                                        The engine refused the delete — it is used by:
                                    </span>
                                    <RefList items={refs} tone="danger" />
                                    <span style={{
                                        display: 'block',
                                        marginTop: 10
                                    }}>
                                        Deleting anyway removes it from role grants and
                                        the app&apos;s journeys list automatically, but
                                        other DEFINITIONS are never auto-edited — they
                                        would be left broken until you fix them.
                                    </span>
                                </>
                            )}
                            {stage === 'dangling' && (
                                <>
                                    <span>
                                        The document is gone and its grants were cleaned.
                                        These definitions still reference it and now need
                                        your attention:
                                    </span>
                                    <RefList items={dangling} tone="warning" />
                                </>
                            )}
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    {stage !== 'dangling' && (
                        <button className="aps-btn aps-btn-ghost" disabled={busy}
                            onClick={() => onOpenChange(false)}>
                            Cancel
                        </button>
                    )}
                    {stage === 'confirm' && (
                        <button className="aps-btn aps-btn-danger" disabled={busy}
                            onClick={() => run(false)}>
                            {busy ? 'Working…' : 'Delete'}
                        </button>
                    )}
                    {stage === 'referenced' && (
                        <button className="aps-btn aps-btn-danger" disabled={busy}
                            onClick={() => run(true)}>
                            {busy ? 'Working…' : 'Delete anyway & clean grants'}
                        </button>
                    )}
                    {stage === 'dangling' && (
                        <button className="aps-btn aps-btn-primary"
                            onClick={() => {
                                onOpenChange(false);
                                onDeleted();
                            }}>
                            Understood
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
